import { FC, useState, useMemo } from 'react';
import {
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  TextField,
  InputAdornment,
  TablePagination,
  IconButton,
  Tooltip,
} from '@material-ui/core';
import { Person, BusinessCenter, Group, Domain, Search, GetApp } from '@material-ui/icons';
import { useStyles } from './styles';
import { CircularProgress } from '@material-ui/core';
import { useStore } from 'hooks/useRootStore';
import { handleCompanyUrl } from 'utils/organizationUtils';
import { useMutation } from 'react-query';
import { useHistory } from 'react-router-dom';
import * as XLSX from 'xlsx';
interface TimelineProps {
  events?: any;
  loading?: boolean;
  userName?: string;
  userEmail?: string;
  header?: string;
}

const AppTimeline: FC<TimelineProps> = ({ events = [], loading = false, header, userName, userEmail }) => {
  const classes = useStyles();
  const {
    ldapStore: { viewTimelines, viewGoogleTimelineUser, viewTimelineAdmin },
    notificationStore: { openNotification },
  } = useStore();

  const [filterText, setFilterText] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  // let timelineEvents: any[] = [];
  // let displayName = userName;
  // // let displayEmail = userEmail;
  // if (events && typeof events === 'object' && events.timeline && Array.isArray(events.timeline.data)) {
  //   timelineEvents = events.timeline.data;
  //   displayName = displayName || events.app_name || events.name;
  //   // displayEmail = displayEmail || events.email;
  // } else if (Array.isArray(events)) {
  //   timelineEvents = events;
  // }
  const { timelineEvents, displayName } = useMemo(() => {
    let timelineEvents: any[] = [];
    let displayName = userName;

    if (events && typeof events === 'object' && events.timeline && Array.isArray(events.timeline.data)) {
      timelineEvents = events.timeline.data;
      displayName = displayName || events.app_name || events.name;
    } else if (Array.isArray(events)) {
      timelineEvents = events;
    }

    return {
      timelineEvents,
      displayName,
    };
  }, [events, userName]);

  const filteredEvents = useMemo(() => {
    if (!filterText.trim()) return timelineEvents;
    const q = filterText.toLowerCase();
    return timelineEvents.filter(
      (e: any) =>
        (e.raw_description || '').toLowerCase().includes(q) ||
        (e.type || '').toLowerCase().includes(q) ||
        (e.created_at || e.createdAt || '').toLowerCase().includes(q)
    );
  }, [timelineEvents, filterText]);

  const paginatedEvents = useMemo(
    () => filteredEvents.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredEvents, page, rowsPerPage]
  );

  const handleExportExcel = () => {
    const rows = filteredEvents.map((e: any) => ({
      Type: e.type || '',
      Description: e.raw_description || '',
      Date: e.created_at
        ? new Date(e.created_at).toLocaleString()
        : e.createdAt
        ? new Date(e.createdAt).toLocaleString()
        : e.createtimestamp
        ? new Date(e.createtimestamp).toLocaleString()
        : '',
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Timeline');
    XLSX.writeFile(wb, `timeline_${displayName || 'export'}.xlsx`);
  };

  const getIcon = (type: string) => {
    switch (type) {
      case 'user':
        return <Person />;
      case 'app':
        return <BusinessCenter />;
      case 'department':
        return <Domain />;
      case 'group':
        return <Group />;
      default:
        return <Person />;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'user':
        return 'primary';
      case 'app':
        return 'secondary';
      case 'department':
        return 'default';
      case 'group':
        return 'primary';
      default:
        return 'default';
    }
  };

  const getClickedUser = useMutation('viewUser', (ClientId: number) => viewGoogleTimelineUser(ClientId, header), {
    onSuccess: (user) => {
      let url;

      if (user?.data?.message === 'End User Group and Department') {
        url = `${handleCompanyUrl('/admin/profile')}`;
        window.open(url, '_blank');
      }
      localStorage.setItem('ClickedUser', JSON.stringify(user.data.user));
      if (user?.data?.message === 'User found') {
        url = `${handleCompanyUrl('/user/view')}`;
        window.open(url, '_blank');
      } else if (user?.data?.message === 'Group found') {
        url = `${handleCompanyUrl('/group/users')}`;
        window.open(url, '_blank');
      } else if (user?.data?.message === 'Department found') {
        url = `${handleCompanyUrl('/department/users')}`;
        window.open(url, '_blank');
      } else {
        url = `${handleCompanyUrl('/admin/profile')}`;
        window.open(url, '_blank');
      }
      window.open(url, '_blank');
    },
    onError: (error: any) => {
      openNotification('error', error.data?.message || error.message || 'User not found');
    },
  });
  const getClicked = useMutation(
    'viewUser',
    ({ clientId, entityType }: { clientId: number; entityType: 'user' | 'group' | 'department' }) =>
      viewTimelines(clientId, entityType),
    {
      onSuccess: (user) => {
        let url;

        localStorage.setItem('ClickedUser', JSON.stringify(user.data.user));

        if (user?.data?.message === 'User found') {
          url = handleCompanyUrl('/user/view');
        } else if (user?.data?.message === 'Group found') {
          url = handleCompanyUrl('/group/users');
        } else if (user?.data?.message === 'Department found') {
          url = handleCompanyUrl('/department/users');
        } else {
          url = handleCompanyUrl('/admin/profile');
        }

        window.open(url, '_blank');
      },

      onError: (error: any) => {
        openNotification('error', error.data?.message || error.message || 'User not found');
      },
    }
  );

  const getClickedAdmin = useMutation('viewUser', (ClientId: number) => viewTimelineAdmin(ClientId), {
    onSuccess: (user) => {
      let url;
      if (user?.data?.message === 'End User Group and Department') {
        url = `${handleCompanyUrl('/admin/profile')}`;
        window.open(url, '_blank');
      } else {
        url = `${handleCompanyUrl('/admin/profile')}`;
        window.open(url, '_blank');
      }
    },
    onError: (error: any) => {
      openNotification('error', error.data?.message || error.message || 'User not found');
    },
  });

  const history = useHistory();

  const APP_ROUTES: Record<string, string> = {
    google: '/apps/google',
    zoom: '/apps/zoom',
    microsoft: '/apps/microsoft',
    github: '/apps/github',
  };

  const profileFromStorage = JSON.parse(localStorage.getItem('Profile') || 'null');

  const renderDescription = (event: any) => {
    const desc: string = event.description || '';
    if (
      desc.includes('End-User') ||
      desc.includes('Email:') ||
      desc.includes('First Name:') ||
      desc.includes('Last Name:') ||
      desc.includes('User') ||
      desc.includes('Department') ||
      desc.includes('Group') ||
      desc.includes('App ') ||
      desc.includes('Google') ||
      desc.includes('Zoom') ||
      desc.includes('Microsoft') ||
      desc.includes('Github')
    ) {
      return renderUserSyncDescription(desc, event);
    }

    if (!desc) return null;
    const nodes: React.ReactNode[] = [];

    return <>{nodes}</>;
  };

  const renderUserSyncDescription = (desc: string, event: any) => {
    const nodes: React.ReactNode[] = [];

    const ls: React.CSSProperties = {
      color: '#1976d2',
      cursor: 'pointer',
      textDecoration: 'underline',
    };

    // "by <actor>" ko alag kar do (same as your current logic)
    const byIndex = desc.lastIndexOf(' by ');
    const mainPart = byIndex !== -1 ? desc.slice(0, byIndex) : desc;
    const actorPart = byIndex !== -1 ? desc.slice(byIndex + 4) : null;

    const regex =
      /(End-User\s+)([^\s[]+)|(Email:\s*)([^,\]]+)|(First Name:\s*)([^,\]]+)|(Middle Name:\s*)([^,\]]+)|(Last Name:\s*)([^,\]]+)|(Google|Zoom|Microsoft|Github|GitHub)|(\(([^)]+)\))/gi;

    let lastIndex = 0;
    let match: any;

    while ((match = regex.exec(mainPart)) !== null) {
      if (match.index > lastIndex) {
        nodes.push(<span key={`text-${lastIndex}`}>{mainPart.slice(lastIndex, match.index)}</span>);
      }

      const userId = event?.user_id || event?.group_id || event?.department_id || event?.entity_id;

      // End-User email
      if (match[1] && match[2]) {
        nodes.push(<span key={`label-${match.index}`}>{match[1]}</span>);

        nodes.push(
          <span
            key={`email-${match.index}`}
            style={ls}
            onClick={() => {
              if (userId) getClickedUser.mutate(userId);
            }}
          >
            {match[2]}
          </span>
        );
      }

      // Email value
      else if (match[3] && match[4]) {
        nodes.push(<span key={`email-label-${match.index}`}>{match[3]}</span>);

        nodes.push(
          <span
            key={`email-value-${match.index}`}
            style={ls}
            onClick={() => {
              if (userId) getClickedUser.mutate(userId);
            }}
          >
            {match[4]}
          </span>
        );
      }

      // First Name value
      else if (match[5] && match[6]) {
        nodes.push(<span key={`fname-label-${match.index}`}>{match[5]}</span>);

        nodes.push(
          <span
            key={`fname-value-${match.index}`}
            style={ls}
            onClick={() => {
              if (userId) getClickedUser.mutate(userId);
            }}
          >
            {match[6]}
          </span>
        );
      }

      // Last Name value
      else if (match[7] && match[8]) {
        nodes.push(<span key={`lname-label-${match.index}`}>{match[7]}</span>);

        nodes.push(
          <span
            key={`lname-value-${match.index}`}
            style={ls}
            onClick={() => {
              if (userId) getClickedUser.mutate(userId);
            }}
          >
            {match[8]}
          </span>
        );
      } else if (match[9] && match[10]) {
        nodes.push(<span key={`mname-label-${match.index}`}>{match[9]}</span>);

        nodes.push(
          <span
            key={`mname-value-${match.index}`}
            style={ls}
            onClick={() => {
              if (userId) getClickedUser.mutate(userId);
            }}
          >
            {match[10]}
          </span>
        );
      } else if (match[11]) {
        const appName = match[11];

        nodes.push(
          <span
            key={`app-${match.index}`}
            style={ls}
            onClick={() => {
              const appKey = appName.toLowerCase();
              const route = APP_ROUTES[appKey] || '/apps';
              history.push(handleCompanyUrl(route));
            }}
          >
            {appName}
          </span>
        );
      } else if (match[12] && match[13]) {
        const entityType = event?.entity_type || 'user';

        const entityId = event?.entity_id || event?.user_id || event?.group_id || event?.department_id;

        nodes.push(
          <span
            key={`bracket-value-${match.index}`}
            style={ls}
            onClick={() => {
              if (entityId) {
                getClicked.mutate({
                  clientId: entityId,
                  entityType,
                });
              }
            }}
          >
            {match[12]}
          </span>
        );
      }

      lastIndex = regex.lastIndex;
    }

    if (lastIndex < mainPart.length) {
      nodes.push(<span key='last'>{mainPart.slice(lastIndex)}</span>);
    }
    // Text inside (...) -> BLUE

    // BY ACTOR (same as your old code)
    if (actorPart) {
      nodes.push(<span key='by'>{' by '}</span>);

      // "." se pehle ka part blue hoga
      const dotIndex = actorPart.indexOf('.');
      const actorName = dotIndex !== -1 ? actorPart.slice(0, dotIndex) : actorPart;
      const actorSuffix = dotIndex !== -1 ? actorPart.slice(dotIndex) : '';

      if (profileFromStorage?.permission !== 'Manager') {
        const actorId = event.actor_id || event.created_by_id || event.admin_id || event.updated_by;

        nodes.push(
          <span key='actor'>
            <span
              style={ls}
              onClick={() => {
                if (actorId) {
                  getClickedAdmin.mutate(actorId);
                }
              }}
            >
              {actorName}
            </span>

            <span>{actorSuffix}</span>
          </span>
        );
      } else {
        nodes.push(<span key='actor'>{actorPart}</span>);
      }
    }

    return <>{nodes}</>;
  };

  return (
    <Paper className={classes.paper}>
      {loading ? (
        <div className={classes.emptyState}>
          <CircularProgress size={40} />
        </div>
      ) : (
        <>
          {/* Toolbar: Filter + Export */}
          <div className={classes.toolbar}>
            <TextField
              size='small'
              placeholder='Search timeline...'
              value={filterText}
              onChange={(e) => {
                setFilterText(e.target.value);
                setPage(0);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <Search fontSize='small' />
                  </InputAdornment>
                ),
              }}
              variant='outlined'
            />
            <Tooltip title='Export as Excel'>
              <IconButton size='small' onClick={handleExportExcel} disabled={filteredEvents.length === 0}>
                <GetApp />
              </IconButton>
            </Tooltip>
          </div>

          {filteredEvents.length === 0 ? (
            <div className={classes.emptyState}>
              <Typography variant='body2' color='textSecondary'>
                No timeline events found
              </Typography>
            </div>
          ) : (
            <>
              <List className={classes.list}>
                {paginatedEvents.map((event: any, index: any) => (
                  <ListItem key={event.id || index} className={classes.listItem}>
                    <ListItemAvatar>
                      <Avatar className={classes.avatar}>{getIcon(event.type || 'user')}</Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <div className={classes.primaryText}>
                          <Typography
                            component='span'
                            // style={{ cursor: 'pointer', color: '#1976d2', fontWeight: 500 }}
                            // onClick={() => getClickedUser.mutate(events.user_id || events.group_id || events.department_id)}
                          >
                            {event.title || 'Unknown User'}
                          </Typography>
                          <Chip
                            label={event.type || 'activity'}
                            size='small'
                            color={getTypeColor(event.type || 'user') as any}
                            className={classes.typeChip}
                          />
                        </div>
                      }
                      secondary={
                        <div className={classes.secondaryText}>
                          {/* {event.created_by_email && (
                          
                            <Typography variant="body2" color="textSecondary">
                              Email: <span style={{ cursor: 'pointer', color: '#1976d2', fontWeight: 500 }}
                              onClick={() => getClickedUser.mutate(events.user_id || events.group_id || events.department_id)}> {event.created_by_email } </span> 
                            </Typography>
                            

                          )} */}
                          <div className={classes.secondaryText}>
                            {event.description && (
                              <Typography variant='body2' className={classes.description}>
                                {renderDescription(event)}
                              </Typography>
                            )}
                            <Typography variant='caption' color='textSecondary'>
                              {event.created_at
                                ? new Date(event.created_at).toLocaleString()
                                : event.createdAt
                                ? new Date(event.createdAt).toLocaleString()
                                : event.createtimestamp
                                ? new Date(event.createtimestamp).toLocaleString()
                                : 'Unknown date'}
                            </Typography>
                          </div>
                        </div>
                      }
                    />
                  </ListItem>
                ))}
              </List>
              <TablePagination
                component='div'
                count={filteredEvents.length}
                page={page}
                onChangePage={(_: any, newPage: number) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onChangeRowsPerPage={(e: any) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[5, 10, 25, 50]}
              />
            </>
          )}
        </>
      )}
    </Paper>
  );
};

export default AppTimeline;
